import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Star, Share2 } from "lucide-react";
import { api } from "../lib/api";
import { formatDateLong } from "../lib/padel";
import { Avatar } from "./Avatar";
import { Button } from "./ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";

const SCORES = Array.from({ length: 19 }, (_, i) => (1 + i * 0.5).toString());
export const fmtScore = (n) => {
  if (n == null) return "–";
  return (Number.isInteger(n) ? String(n) : n.toFixed(2).replace(/0+$/, "")).replace(".", ",");
};
const slug = (s) => s.toLowerCase().replace(/\s+/g, "-");

export const buildRatingsText = (match, players) => {
  const r = match.result;
  const lines = [`📝 *Pagelle · ${match.venue}*`, `📅 ${formatDateLong(match.date)}`];
  if (r) lines.push(`🎾 ${r.team_a.join(" & ")} ${r.sets.map((s) => `${s.a}-${s.b}`).join(" ")} ${r.team_b.join(" & ")}`);
  lines.push("");
  [...players].sort((a, b) => (b.avg ?? -1) - (a.avg ?? -1)).forEach((p) => {
    lines.push(`*${p.name}*: ${fmtScore(p.avg)}${p.count ? ` (${p.ratings.map((x) => `${x.rater} ${fmtScore(x.score)}`).join(", ")})` : ""}`);
  });
  return lines.join("\n");
};

const shareRatings = (match, players) =>
  window.open(`https://wa.me/?text=${encodeURIComponent(buildRatingsText(match, players))}`, "_blank");

const PlayerRow = ({ p, photo, editable, value, onChange }) => (
  <div className="rounded-xl bg-[#0b1420] border border-[#1e2c4a] p-3" data-testid={`rating-player-${slug(p.name)}`}>
    <div className="flex items-center gap-3">
      <Avatar name={p.name} photoPath={photo} size={36} />
      <div className="flex-1 min-w-0">
        <p className="font-semibold text-slate-100 truncate">{p.name}</p>
        <p className="text-xs text-slate-400">{p.count ? `${p.count} vot${p.count === 1 ? "o" : "i"}` : "Nessun voto"}</p>
      </div>
      <span className="font-heading font-extrabold text-2xl text-[#ccff00]" data-testid={`rating-avg-${slug(p.name)}`}>{fmtScore(p.avg)}</span>
    </div>
    {p.ratings.length > 0 && (
      <p className="text-xs text-slate-400 mt-2" data-testid={`rating-details-${slug(p.name)}`}>
        {p.ratings.map((r) => `${r.rater}: ${fmtScore(r.score)}`).join(" · ")}
      </p>
    )}
    {editable && (
      <Select value={value || ""} onValueChange={onChange}>
        <SelectTrigger data-testid={`rating-select-${slug(p.name)}`} className="mt-2 bg-[#121b2d] border-[#1e2c4a] text-slate-100 h-10 rounded-lg">
          <SelectValue placeholder="Il tuo voto" />
        </SelectTrigger>
        <SelectContent className="bg-[#121b2d] border-[#1e2c4a] text-slate-100 max-h-64">
          {SCORES.map((s) => (
            <SelectItem key={s} value={s} data-testid={`rating-option-${slug(p.name)}-${s}`}>{s.replace(".", ",")}</SelectItem>
          ))}
        </SelectContent>
      </Select>
    )}
  </div>
);

export const RatingsDialog = ({ match, playerName, photos = {}, onClose }) => {
  const [data, setData] = useState(null);
  const [draft, setDraft] = useState({});
  const [saving, setSaving] = useState(false);
  const me = (playerName || "").toLowerCase();

  const load = () => match && api.matchRatings(match.id).then((d) => {
    setData(d);
    setDraft(Object.fromEntries(Object.entries(d.my_ratings).map(([k, v]) => [k, String(v)])));
  }).catch(() => toast.error("Impossibile caricare le pagelle"));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { setData(null); load(); }, [match?.id]);

  const save = async () => {
    setSaving(true);
    try {
      await api.saveRatings(match.id, Object.fromEntries(Object.entries(draft).map(([k, v]) => [k, parseFloat(v)])));
      toast.success("Pagelle salvate!");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Salvataggio non riuscito");
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={!!match} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="bg-[#121b2d] border-[#1e2c4a] text-slate-100 max-w-md max-h-[90vh] overflow-y-auto" data-testid="ratings-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl flex items-center gap-2">
            <Star className="w-5 h-5 text-[#ccff00]" /> Pagelle
          </DialogTitle>
          <DialogDescription className="text-slate-400">
            {match?.venue} · voti da 1 a 10. {data?.can_rate ? "Vota i tuoi compagni e avversari." : "Solo chi ha giocato può votare."}
          </DialogDescription>
        </DialogHeader>
        {!data ? <p className="text-sm text-slate-500">Caricamento…</p> : (
          <div className="space-y-2">
            {data.players.map((p) => (
              <PlayerRow key={p.name} p={p} photo={photos[p.name.toLowerCase()]}
                editable={data.can_rate && p.name.toLowerCase() !== me}
                value={draft[p.name]} onChange={(v) => setDraft((d) => ({ ...d, [p.name]: v }))} />
            ))}
            {data.can_rate && (
              <Button onClick={save} disabled={saving || !Object.keys(draft).length} data-testid="ratings-save-btn"
                className="w-full bg-[#ccff00] hover:bg-[#aeea00] text-[#090d16] font-bold rounded-full h-11 mt-2">
                Salva voti
              </Button>
            )}
            {data.players.some((p) => p.count > 0) && (
              <Button onClick={() => shareRatings(match, data.players)} data-testid="ratings-share-btn"
                className="w-full bg-[#25D366] hover:bg-[#20bd5a] text-white font-bold rounded-full h-11">
                <Share2 className="w-4 h-4 mr-1.5" /> Condividi pagelle su WhatsApp
              </Button>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
